import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  DollarSign,
  HelpCircle,
  ArrowRight,
  Car,
  FileCheck,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import '../AllCss/Finance.css';

const CALC_MODES = { monthlyPayment: 'monthlyPayment', vehiclePrice: 'vehiclePrice' };

const GET_FINANCED_PATH = '/get-financed';
const LENDMARK_LOGO = '/images/lendmark-financial-services.png';

const PARTNER_BENEFITS = [
  { icon: Sparkles, key: 'competitiveRates' },
  { icon: FileCheck, key: 'simpleProcess' },
  { icon: Car, key: 'driveSooner' },
];

/** USD summary lines: 0.99 → 9.99 → 99.99 → 999.99 → 9,999.99 (2 decimals, comma ≥ 1,000) */
function formatUsdTwoDecimals(n) {
  if (n == null || !Number.isFinite(Number(n))) return '';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function Finance() {
  const { t } = useLocale();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const preQualLinkTo = isAuthenticated
    ? GET_FINANCED_PATH
    : `/auth?redirect=${encodeURIComponent(GET_FINANCED_PATH)}`;
  const [calcMode, setCalcMode] = useState(CALC_MODES.monthlyPayment);
  const [loanAmount, setLoanAmount] = useState(25000);
  const [downPayment, setDownPayment] = useState(2500);
  const [targetMonthlyPayment, setTargetMonthlyPayment] = useState(399);
  const [loanTerm, setLoanTerm] = useState(72);
  const [interestRate, setInterestRate] = useState(5.49);
  const [state, setState] = useState('AL');
  const [creditScore, setCreditScore] = useState('excellent');

  // Function to get state from zipcode
  const getStateFromZipcode = (zipcode) => {
    const zip = parseInt(zipcode);
    if (isNaN(zip)) return 'CA';

    // Zipcode to state mapping based on US postal code ranges
    if (zip >= 35000 && zip <= 36999) return 'AL';
    if (zip >= 99500 && zip <= 99999) return 'AK';
    if (zip >= 85000 && zip <= 86999) return 'AZ';
    if (zip >= 71600 && zip <= 72999) return 'AR';
    if (zip >= 90000 && zip <= 96699) return 'CA';
    if (zip >= 80000 && zip <= 81999) return 'CO';
    if ((zip >= 6000 && zip <= 6999)) return 'CT';
    if (zip >= 19700 && zip <= 19999) return 'DE';
    if (zip >= 32000 && zip <= 34999) return 'FL';
    if ((zip >= 30000 && zip <= 31999) || (zip >= 39800 && zip <= 39999)) return 'GA';
    if (zip >= 96700 && zip <= 96899) return 'HI';
    if (zip >= 83200 && zip <= 83999) return 'ID';
    if (zip >= 60000 && zip <= 62999) return 'IL';
    if (zip >= 46000 && zip <= 47999) return 'IN';
    if (zip >= 50000 && zip <= 52999) return 'IA';
    if (zip >= 66000 && zip <= 67999) return 'KS';
    if (zip >= 40000 && zip <= 42999) return 'KY';
    if (zip >= 70000 && zip <= 71599) return 'LA';
    if (zip >= 3900 && zip <= 4999) return 'ME';
    if (zip >= 20600 && zip <= 21999) return 'MD';
    if ((zip >= 1000 && zip <= 2799) || zip === 5501 || zip === 5544) return 'MA';
    if (zip >= 48000 && zip <= 49999) return 'MI';
    if (zip >= 55000 && zip <= 56999) return 'MN';
    if (zip >= 38600 && zip <= 39999) return 'MS';
    if (zip >= 63000 && zip <= 65999) return 'MO';
    if (zip >= 59000 && zip <= 59999) return 'MT';
    if (zip >= 68000 && zip <= 69999) return 'NE';
    if (zip >= 88900 && zip <= 89999) return 'NV';
    if (zip >= 3000 && zip <= 3899) return 'NH';
    if (zip >= 7000 && zip <= 8999) return 'NJ';
    if (zip >= 87000 && zip <= 88499) return 'NM';
    if ((zip >= 10000 && zip <= 14999) || zip === 6390 || zip === 501 || zip === 544) return 'NY';
    if (zip >= 27000 && zip <= 28999) return 'NC';
    if (zip >= 58000 && zip <= 58999) return 'ND';
    if (zip >= 43000 && zip <= 45999) return 'OH';
    if ((zip >= 73000 && zip <= 73199) || (zip >= 73400 && zip <= 74999)) return 'OK';
    if (zip >= 97000 && zip <= 97999) return 'OR';
    if (zip >= 15000 && zip <= 19699) return 'PA';
    if (zip >= 2800 && zip <= 2999) return 'RI';
    if (zip >= 29000 && zip <= 29999) return 'SC';
    if (zip >= 57000 && zip <= 57999) return 'SD';
    if (zip >= 37000 && zip <= 38599) return 'TN';
    if ((zip >= 75000 && zip <= 79999) || (zip >= 73301 && zip <= 73399) || (zip >= 88500 && zip <= 88599)) return 'TX';
    if (zip >= 84000 && zip <= 84999) return 'UT';
    if (zip >= 5000 && zip <= 5999) return 'VT';
    if ((zip >= 20100 && zip <= 20199) || (zip >= 22000 && zip <= 24699)) return 'VA';
    if (zip >= 98000 && zip <= 99499) return 'WA';
    if (zip >= 24700 && zip <= 26999) return 'WV';
    if (zip >= 53000 && zip <= 54999) return 'WI';
    if ((zip >= 82000 && zip <= 83199) || zip === 83414) return 'WY';

    return 'CA'; // Default to California
  };

  // Auto-detect state from zipcode in navbar
  useEffect(() => {
    const savedZipcode = localStorage.getItem('selectedZipcode');
    if (savedZipcode) {
      const detectedState = getStateFromZipcode(savedZipcode);
      setState(detectedState);
    }

    // Listen for zipcode changes from navbar
    const handleZipcodeChange = (event) => {
      const newZipcode = event.detail.zipcode;
      const detectedState = getStateFromZipcode(newZipcode);
      setState(detectedState);
    };

    window.addEventListener('zipcodeChanged', handleZipcodeChange);
    return () => window.removeEventListener('zipcodeChanged', handleZipcodeChange);
  }, []);

  const monthlyRate = interestRate / 100 / 12;
  const numPayments = loanTerm;

  const calculateMonthlyPayment = (principal) => {
    if (principal <= 0 || monthlyRate === 0) return 0;
    const p = (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
      (Math.pow(1 + monthlyRate, numPayments) - 1);
    return Number(p.toFixed(2));
  };

  const calculateVehiclePriceFromPayment = () => {
    if (monthlyRate <= 0) return downPayment;
    const factor = (Math.pow(1 + monthlyRate, numPayments) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, numPayments));
    const loanPrincipal = targetMonthlyPayment * factor;
    return loanPrincipal + downPayment;
  };

  const estimatedVehiclePrice = calculateVehiclePriceFromPayment();
  const principalInPaymentMode = loanAmount - downPayment;
  const monthlyPayment = calcMode === CALC_MODES.vehiclePrice
    ? targetMonthlyPayment
    : calculateMonthlyPayment(principalInPaymentMode);
  const displayVehiclePrice = calcMode === CALC_MODES.vehiclePrice ? estimatedVehiclePrice : loanAmount;
  const estTaxFees = 1500;
  const summaryTotal = (displayVehiclePrice - downPayment) + estTaxFees;

  return (
    <div className="finance-page">
      <Navbar />

      {/* Lendmark partner spotlight */}
      <section className="finance-partner-section" aria-labelledby="finance-partner-heading">
        <div className="finance-partner-container finance-partner-layout">
            <div className="finance-partner-content">
              <h2 id="finance-partner-heading" className="finance-partner-title">
                {t('finance.partner.title')}
              </h2>
              <p className="finance-partner-lead">{t('finance.partner.lead')}</p>
              <ul className="finance-partner-benefits">
                {PARTNER_BENEFITS.map(({ icon: Icon, key }) => (
                  <li key={key} className="finance-partner-benefit">
                    <span className="finance-partner-benefit-icon" aria-hidden>
                      <Icon size={18} strokeWidth={2.25} />
                    </span>
                    <span>
                      <strong>{t(`finance.partner.benefits.${key}.title`)}</strong>
                      {' — '}
                      {t(`finance.partner.benefits.${key}.desc`)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="finance-partner-actions">
                <Link to={preQualLinkTo} className="finance-partner-cta finance-partner-cta--primary">
                  {t('finance.checkYourRate')}
                  <ArrowRight size={18} aria-hidden />
                </Link>
                <a
                  href="https://www.lendmarkfinancial.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="finance-partner-cta finance-partner-cta--secondary"
                >
                  {t('finance.partner.learnMore')}
                </a>
              </div>
            </div>
            <div className="finance-partner-visual">
              <div className="finance-partner-logo-wrap">
                <img
                  src={LENDMARK_LOGO}
                  alt={t('finance.partner.logoAlt')}
                  className="finance-partner-logo"
                  width={280}
                  height={80}
                />
              </div>
              <div className="finance-partner-stats">
                <div className="finance-partner-stat">
                  <span className="finance-partner-stat-value">50+</span>
                  <span className="finance-partner-stat-label">{t('finance.partner.stats.years')}</span>
                </div>
                <div className="finance-partner-stat">
                  <span className="finance-partner-stat-value">300+</span>
                  <span className="finance-partner-stat-label">{t('finance.partner.stats.locations')}</span>
                </div>
                <div className="finance-partner-stat">
                  <span className="finance-partner-stat-value">1M+</span>
                  <span className="finance-partner-stat-label">{t('finance.partner.stats.customers')}</span>
                </div>
              </div>
              <p className="finance-partner-note">{t('finance.partner.note')}</p>
            </div>
        </div>
      </section>

      {/* Interactive Calculator */}
      <section id="finance-calculator" className="finance-calculator-section" aria-labelledby="finance-calc-title">
        <div className="finance-calculator-container">
          <header className="finance-calculator-header">
            <h2 id="finance-calc-title" className="finance-calc-title">{t('finance.paymentCalculator')}</h2>
            <p className="finance-calc-subtitle">{t('finance.calcSubtitle')}</p>
          </header>
          
          {/* Tabs: Monthly Payment | Vehicle Price */}
          <div className="calculator-tabs" role="tablist" aria-label="Calculator mode">
            <button
              type="button"
              role="tab"
              aria-selected={calcMode === CALC_MODES.monthlyPayment}
              aria-controls="calculator-panel"
              id="tab-monthly"
              className={`calculator-tab ${calcMode === CALC_MODES.monthlyPayment ? 'calculator-tab--active' : ''}`}
              onClick={() => setCalcMode(CALC_MODES.monthlyPayment)}
            >
              Monthly Payment
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={calcMode === CALC_MODES.vehiclePrice}
              aria-controls="calculator-panel"
              id="tab-vehicle"
              className={`calculator-tab ${calcMode === CALC_MODES.vehiclePrice ? 'calculator-tab--active' : ''}`}
              onClick={() => setCalcMode(CALC_MODES.vehiclePrice)}
            >
              Vehicle Price
            </button>
          </div>

          <div id="calculator-panel" className="calculator-wrapper" role="tabpanel" aria-labelledby={calcMode === CALC_MODES.monthlyPayment ? 'tab-monthly' : 'tab-vehicle'}>
            {/* Left Column: White card with inputs (match reference) */}
            <div className="calculator-inputs-wrap">
              <div className="calculator-inputs-card">
                <div className="calculator-inputs">
              {calcMode === CALC_MODES.monthlyPayment ? (
                <div className="input-group">
                  <label htmlFor="loanAmount">Vehicle price</label>
                  <div className="input-with-icon">
                    <DollarSign size={18} className="input-icon" />
                    <input
                      type="number"
                      id="loanAmount"
                      value={loanAmount}
                      onChange={(e) => setLoanAmount(Number(e.target.value))}
                      min="5000"
                      max="150000"
                      step="1000"
                    />
                  </div>
                </div>
              ) : (
                <div className="input-group">
                  <label htmlFor="targetPayment">Monthly payment</label>
                  <div className="input-with-icon">
                    <DollarSign size={18} className="input-icon" />
                    <input
                      type="number"
                      id="targetPayment"
                      value={targetMonthlyPayment}
                      onChange={(e) => setTargetMonthlyPayment(Number(e.target.value))}
                      min="50"
                      max="2000"
                      step="25"
                    />
                  </div>
                </div>
              )}

              <div className="input-group">
                <label htmlFor="downPayment">Down Payment</label>
                <div className="input-with-icon">
                  <DollarSign size={18} className="input-icon" />
                  <input
                    type="number"
                    id="downPayment"
                    value={downPayment}
                    onChange={(e) => setDownPayment(Number(e.target.value))}
                    min="0"
                    max={calcMode === CALC_MODES.vehiclePrice ? estimatedVehiclePrice : loanAmount}
                    step="500"
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="creditScore">Credit Score</label>
                <select
                  id="creditScore"
                  value={creditScore}
                  onChange={(e) => setCreditScore(e.target.value)}
                >
                  <option value="excellent">Excellent (800+ FICO® Score)</option>
                  <option value="great">Great (740-799 FICO® Score)</option>
                  <option value="good">Good (670-739 FICO® Score)</option>
                  <option value="fair">Fair (580-669 FICO® Score)</option>
                  <option value="poor">Poor (300-579 FICO® Score)</option>
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="loanTerm">Term length</label>
                <select
                  id="loanTerm"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                >
                  <option value="36">36 months</option>
                  <option value="48">48 months</option>
                  <option value="60">60 months</option>
                  <option value="72">72 months</option>
                  <option value="84">84 months</option>
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="interestRate">APR</label>
                <div className="input-with-icon">
                  <span className="input-icon percent-icon">%</span>
                  <input
                    type="number"
                    id="interestRate"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    min="0"
                    max="25"
                    step="0.1"
                  />
                </div>
                <p className="input-note">Estimated based on your credit score.</p>
              </div>
            </div>
              </div>
            </div>

            {/* Right Column: Summary card */}
            <div className="calculator-summary">
              <div className="summary-card summary-card--dark">
                <h3 className="summary-title">Summary</h3>
                <div className="summary-breakdown">
                  {calcMode === CALC_MODES.vehiclePrice ? (
                    <div className="breakdown-row">
                      <span>Monthly payment</span>
                      <span className="breakdown-value">${formatUsdTwoDecimals(targetMonthlyPayment)}</span>
                    </div>
                  ) : (
                    <div className="breakdown-row">
                      <span>Vehicle price</span>
                      <span className="breakdown-value">${formatUsdTwoDecimals(displayVehiclePrice)}</span>
                    </div>
                  )}
                  <div className="breakdown-row">
                    <span>Down payment</span>
                    <span className="breakdown-value breakdown-value--minus">- ${formatUsdTwoDecimals(downPayment)}</span>
                  </div>
                  <div className="breakdown-row">
                    <span>Est. tax, title & fees ({state})</span>
                    <span className="breakdown-value breakdown-value--plus">+ ${formatUsdTwoDecimals(estTaxFees)}</span>
                  </div>
                  <div className="breakdown-row breakdown-row--total">
                    <span>{calcMode === CALC_MODES.vehiclePrice ? 'Loan + fees' : 'Total'}</span>
                    <span className="breakdown-value">${formatUsdTwoDecimals(summaryTotal)}</span>
                  </div>
                </div>
                <div className="summary-result">
                  <div className="result-label-with-icon">
                    <span>
                      {calcMode === CALC_MODES.vehiclePrice ? 'Estimated vehicle price' : 'Estimated monthly payment'}
                    </span>
                    <HelpCircle size={14} className="result-info-icon" aria-hidden />
                  </div>
                  <div className="result-amount-main">
                    {calcMode === CALC_MODES.vehiclePrice
                      ? `$${formatUsdTwoDecimals(displayVehiclePrice)}`
                      : `$${formatUsdTwoDecimals(monthlyPayment)}`}
                  </div>
                </div>
                <div className="summary-ctas">
                  <Link to="/inventory" className="summary-cta summary-cta--primary">
                    Shop With Budget
                  </Link>
                  <Link to={preQualLinkTo} className="summary-cta summary-cta--secondary">
                    {t('finance.getPreQualified')}
                  </Link>
                </div>
                <p className="summary-disclaimer">Rates and terms subject to credit approval. Final terms may vary.</p>
              </div>
            </div>
          </div>
        </div>
        
      </section>

      {/* How Financing Works – professional block */}
      <section className="finance-how-section" aria-labelledby="finance-how-heading">
        <div className="finance-how-container">
          <header className="finance-how-header">
            <h2 id="finance-how-heading" className="finance-how-title">{t('finance.how.title')}</h2>
            <p className="finance-how-lead">{t('finance.how.lead')}</p>
          </header>
          <ol className="finance-how-steps">
            {['apply', 'checkRate', 'shop', 'drive'].map((step, i) => (
              <li key={step} className="finance-how-step">
                <div className="finance-how-marker" aria-hidden>{i + 1}</div>
                <div className="finance-how-body">
                  <h3 className="finance-how-step-title">{t(`finance.how.steps.${step}.title`)}</h3>
                  <p className="finance-how-step-desc">{t(`finance.how.steps.${step}.desc`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="finance-cta-section" aria-labelledby="finance-cta-heading">
        <div className="finance-cta-inner">
          <div className="finance-cta-content">
            <img
              src={LENDMARK_LOGO}
              alt=""
              className="finance-cta-logo"
              width={160}
              height={44}
              aria-hidden
            />
            <h2 id="finance-cta-heading" className="finance-cta-title">{t('finance.cta.title')}</h2>
            <p className="finance-cta-lead">{t('finance.cta.lead')}</p>
            <Link to={preQualLinkTo} className="finance-cta-button">
              {t('finance.getPreQualified')}
              <ArrowRight size={20} aria-hidden />
            </Link>
            <p className="finance-cta-note">{t('finance.cta.note')}</p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default Finance;

